import React from "react";
import { format } from "date-fns";
import { Visit } from "../../../interfaces/Visit";
import { useNotification } from "../../../NotificationContext";
import { deleteVisitEntry } from "../../../apis/visit";

interface PharmacyItem {
  name: string;
  quantity: number;
}

interface IPDEntry {
  id?: string | number;
  type: string;
  date: string;
  data: any;
  userId?: number;
}

interface IPDEntryCardProps {
  entry: IPDEntry;
  visit: Visit | undefined;
  onEdit: () => void;
  onRefresh: () => void;
}

const IPDEntryCard: React.FC<IPDEntryCardProps> = ({
  entry,
  visit,
  onEdit,
  onRefresh,
}) => {
  const { showNotification } = useNotification();

  const handleDelete = async () => {
    if (!visit || !entry.id) return;

    if (
      window.confirm(
        "Are you sure you want to delete this entry? This action cannot be undone."
      )
    ) {
      try {
        const entryId = typeof entry.id === 'string' ? parseInt(entry.id) : entry.id;
        await deleteVisitEntry(visit.id!, entryId);
        showNotification(
          "success",
          "Entry deleted successfully",
          "",
          "topRight"
        );
        onRefresh();
      } catch (error: any) {
        console.error("Failed to delete entry:", error);
        const errorMessage = error?.response?.data?.message || "Failed to delete entry";
        showNotification(
          "error",
          errorMessage,
          "",
          "topRight"
        );
      }
    }
  };

  const getEntryTitle = () => {
    const titles: { [key: string]: string } = {
      assessment: "Assessment",
      doctor_rounds: "Doctor Rounds",
      nursing: "Nursing",
      pharmacy_request: "Pharmacy Request",
      lab_request: "Lab Request",
      room_transfer: "Room Transfer",
      payment: "Payment",
      admission: "Admission",
      discharge: "Discharge",
    };
    console.log( entry);
    
    return titles[entry.type] || entry.type;
  };

  const renderEntryContent = () => {
    switch (entry.type) {
      case "assessment":
        return (
          <div className="space-y-2">
            {entry.data?.vitals && (
              <div>
                <h4 className="font-medium text-gray-700">Vitals</h4>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {entry.data.vitals.pulse && (
                    <div>
                      <span className="text-sm text-gray-600">Pulse: </span>
                      <span className="text-sm font-medium">
                        {entry.data.vitals.pulse} bpm
                      </span>
                    </div>
                  )}
                  {entry.data.vitals.bp && (
                    <div>
                      <span className="text-sm text-gray-600">BP: </span>
                      <span className="text-sm font-medium">
                        {entry.data.vitals.bp} mmHg
                      </span>
                    </div>
                  )}
                  {entry.data.vitals.temp && (
                    <div>
                      <span className="text-sm text-gray-600">Temp: </span>
                      <span className="text-sm font-medium">
                        {entry.data.vitals.temp} °F
                      </span>
                    </div>
                  )}
                  {entry.data.vitals.spo2 && (
                    <div>
                      <span className="text-sm text-gray-600">SpO2: </span>
                      <span className="text-sm font-medium">
                        {entry.data.vitals.spo2}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
          </div>
        );

      case "doctor_rounds":
        return (
          <div className="space-y-2">
            {entry.data?.observations && (
              <div>
                <h4 className="font-medium text-gray-700">Observations</h4>
                <p className="text-sm text-gray-600">
                  {entry.data.observations}
                </p>
              </div>
            )}
            {entry.data?.plan && (
              <div>
                <h4 className="font-medium text-gray-700">Plan</h4>
                <p className="text-sm text-gray-600">{entry.data.plan}</p>
              </div>
            )}
            {entry.data?.medications && entry.data.medications.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700">Medications</h4>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {entry.data.medications.map((med: string, idx: number) => (
                    <li key={idx}>{med}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case "nursing":
        return (
          <div className="space-y-2">
            {entry.data?.careProvided && (
              <div>
                <h4 className="font-medium text-gray-700">Care Provided</h4>
                <p className="text-sm text-gray-600">
                  {entry.data.careProvided}
                </p>
              </div>
            )}
            {entry.data?.medicationsAdministered &&
              entry.data.medicationsAdministered.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700">
                    Medications Administered
                  </h4>
                  <ul className="list-disc list-inside text-sm text-gray-600">
                    {entry.data.medicationsAdministered.map(
                      (med: string, idx: number) => (
                        <li key={idx}>{med}</li>
                      )
                    )}
                  </ul>
                </div>
              )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
          </div>
        );

      case "pharmacy_request":
        return (
          <div className="space-y-2">
            {(entry.data?.pharmacy_sale_id || entry.data?.pharmacySaleId || entry.data?.saleId) && (
              <div>
                <span className="text-sm text-gray-600">Pharmacy Sale ID: </span>
                <span className="text-sm font-medium">
                  {entry.data.pharmacy_sale_id || entry.data.pharmacySaleId || entry.data.saleId}
                </span>
              </div>
            )}
            {entry.data?.items && entry.data.items.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700">Requested Items</h4>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {entry.data.items.map((item: PharmacyItem, idx: number) => (
                    <li key={idx}>
                      {item.name} - Qty: {item.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
            {entry.data?.status && (
              <div>
                <span className="text-sm text-gray-600">Status: </span>
                <span
                  className={`text-sm font-medium ${
                    entry.data.status === "fulfilled"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {entry.data.status}
                </span>
              </div>
            )}
          </div>
        );

      case "lab_request":
        return (
          <div className="space-y-2">
            {entry.data?.lab_order_id && (
              <div>
                <span className="text-sm text-gray-600">Lab Order ID: </span>
                <span className="text-sm font-medium">
                  {entry.data.lab_order_id}
                </span>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
            {entry.data?.tests && entry.data.tests.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700">Requested Tests</h4>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {entry.data.tests.map((test: string | { name: string }, idx: number) => (
                    <li key={idx}>{typeof test === 'string' ? test : test.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {entry.data?.status && (
              <div>
                <span className="text-sm text-gray-600">Status: </span>
                <span
                  className={`text-sm font-medium ${
                    entry.data.status === "completed"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {entry.data.status}
                </span>
              </div>
            )}
          </div>
        );

      case "room_transfer":
        return (
          <div className="space-y-2">
            {(entry.data?.from_room_id || entry.data?.fromRoomId || entry.data?.fromRoom) && (
              <div>
                <span className="text-sm text-gray-600">From Room: </span>
                <span className="text-sm font-medium">
                  {entry.data.from_room_id || entry.data.fromRoomId || entry.data.fromRoom}
                </span>
              </div>
            )}
            {(entry.data?.to_room_id || entry.data?.toRoomId || entry.data?.toRoom) && (
              <div>
                <span className="text-sm text-gray-600">To Room: </span>
                <span className="text-sm font-medium">
                  {entry.data.to_room_id || entry.data.toRoomId || entry.data.toRoom}
                </span>
              </div>
            )}
            {entry.data?.transfer_date && (
              <div>
                <span className="text-sm text-gray-600">Transfer Date: </span>
                <span className="text-sm font-medium">
                  {format(new Date(entry.data.transfer_date), "dd MMM yyyy, HH:mm")}
                </span>
              </div>
            )}
            {entry.data?.reason && (
              <div>
                <h4 className="font-medium text-gray-700">Reason</h4>
                <p className="text-sm text-gray-600">{entry.data.reason}</p>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
          </div>
        );

      case "payment":
        return (
          <div className="space-y-2">
            {entry.data?.amount && (
              <div>
                <span className="text-sm text-gray-600">Amount: </span>
                <span className="text-sm font-medium">
                  ₹{entry.data.amount}
                </span>
              </div>
            )}
            {(entry.data?.payment_mode || entry.data?.paymentMode) && (
              <div>
                <span className="text-sm text-gray-600">Payment Mode: </span>
                <span className="text-sm font-medium">
                  {entry.data.payment_mode || entry.data.paymentMode}
                </span>
              </div>
            )}
            {(entry.data?.notes || entry.data?.note) && (
              <div>
                <h4 className="font-medium text-gray-700">Note</h4>
                <p className="text-sm text-gray-600">{entry.data.notes || entry.data.note}</p>
              </div>
            )}
          </div>
        );

      case "admission":
        return (
          <div className="space-y-2">
            {entry.data?.admission_date && (
              <div>
                <span className="text-sm text-gray-600">Admission Date: </span>
                <span className="text-sm font-medium">
                  {format(new Date(entry.data.admission_date), "dd MMM yyyy, HH:mm")}
                </span>
              </div>
            )}
            {entry.data?.room_id && (
              <div>
                <span className="text-sm text-gray-600">Room ID: </span>
                <span className="text-sm font-medium">
                  {entry.data.room_id}
                </span>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
          </div>
        );

      case "discharge":
        return (
          <div className="space-y-2">
            {entry.data?.discharge_date && (
              <div>
                <span className="text-sm text-gray-600">Discharge Date: </span>
                <span className="text-sm font-medium">
                  {format(new Date(entry.data.discharge_date), "dd MMM yyyy, HH:mm")}
                </span>
              </div>
            )}
            {entry.data?.discharge_summary && (
              <div>
                <h4 className="font-medium text-gray-700">Discharge Summary</h4>
                <p className="text-sm text-gray-600">{entry.data.discharge_summary}</p>
              </div>
            )}
            {entry.data?.notes && (
              <div>
                <h4 className="font-medium text-gray-700">Notes</h4>
                <p className="text-sm text-gray-600">{entry.data.notes}</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div>
            <pre className="text-xs text-gray-600">
              {JSON.stringify(entry.data, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="pt-2 lg:pt-5">
      <div className="absolute w-3 h-3 bg-primary rounded-full mt-1.5 -left-1.5 border border-white dark:border-gray-900 dark:bg-gray-700"></div>
      <time className="mb-1 text-sm font-normal leading-none text-active dark:text-gray-500">
        {format(new Date(entry.date), "dd MMM yyyy, HH:mm")}
      </time>
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 border-t border-gray-200 sm:px-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {getEntryTitle()}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={onEdit}
                className="px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"  
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>
          </div>
          {renderEntryContent()}
        </div>
      </div>
    </div>
  );
};

export default IPDEntryCard;

